/**
 * Equine Performance Labs Color System
 * 
 * Production-ready color tokens for all portals.
 * Change colors here to update the entire platform.
 */

// UNIFIED COMPONENT COLORS - Change these to update the ENTIRE website
// 
// To test the unified design:
// 1. Change primary.main to any color (e.g., '#7c3aed' for purple)
// 2. Save the file
// 3. ALL dashboards, navigation, and buttons will update instantly!
//
export const unifiedColors = {
  // Primary colors used across ALL components
  primary: {
    main: '#0d9488',        // Change this to update primary color everywhere
    light: '#14b8a6',
    dark: '#0f766e',
    contrast: '#ffffff'
  },
  
  // Secondary colors used across ALL components
  secondary: {
    main: '#5c725c',        // Change this to update secondary color everywhere
    light: '#6b836b',
    dark: '#4d5f4d',
    contrast: '#ffffff'
  },
  
  // Dashboard-specific colors
  dashboard: {
    gradientStart: '#0d9488',     // Change this to update all dashboard headers
    gradientEnd: '#14b8a6',       // Change this to update all dashboard headers
    cardBackground: '#ffffff',
    cardBorder: '#e2e8f0',
    statCardHover: '#f0fdfa',
    decorationOpacity: 0.1
  },
  
  // Button colors
  button: {
    primaryBg: '#0d9488',
    primaryHover: '#0f766e',
    secondaryBg: '#5c725c',
    secondaryHover: '#4d5f4d',
    outlineBorder: '#0d9488',
    outlineHover: 'rgba(13, 148, 136, 0.08)'
  },
  
  // Navigation colors
  navigation: {
    background: '#0d9488',
    backgroundGradient: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
    hover: 'rgba(255, 255, 255, 0.1)',
    active: 'rgba(255, 255, 255, 0.2)',
    text: '#ffffff',
    sidebarBg: '#ffffff',
    sidebarText: '#374151',
    sidebarHover: '#f0fdfa',
    sidebarActive: '#ccfbf1',
    sidebarActiveText: '#0d9488'
  },
  
  // Status colors
  status: {
    warning: '#f59e0b',
    warningLight: '#fef3c7',
    warningDark: '#92400e',
    success: '#10b981',
    successLight: '#d1fae5',
    successDark: '#065f46'
  }
};

// Keep old structure for backwards compatibility
export const dashboardColors = unifiedColors.dashboard;

// Brand Colors
export const brandColors = {
  // Primary Brand
  primary: {
    50: '#f0fdfa',
    100: '#ccfbf1',
    200: '#99f6e4',
    300: '#5eead4',
    400: '#2dd4bf',
    500: '#14b8a6',
    600: '#0d9488', // Main
    700: '#0f766e',
    800: '#115e59',
    900: '#134e4a',
    950: '#042f2e'
  },
  
  // Secondary Brand (Medical Green)
  secondary: {
    50: '#f6f7f6',
    100: '#e3e8e3',
    200: '#c7d2c7',
    300: '#a3b4a3',
    400: '#7a907a',
    500: '#5c725c', // Main
    600: '#485a48',
    700: '#3c4a3c',
    800: '#323c32',
    900: '#2a322a',
    950: '#161a16'
  },
  
  // Tertiary (Professional Dark)
  tertiary: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b', // Main
    900: '#0f172a',
    950: '#020617'
  }
};

// Semantic Colors
export const semanticColors = {
  success: {
    light: '#d1fae5',
    main: '#10b981',
    dark: '#065f46',
    contrastText: '#ffffff'
  },
  
  warning: {
    light: '#fef3c7',
    main: '#f59e0b',
    dark: '#92400e',
    contrastText: '#ffffff'
  },
  
  error: {
    light: '#fee2e2',
    main: '#ef4444',
    dark: '#991b1b',
    contrastText: '#ffffff'
  },
  
  info: {
    light: '#dbeafe',
    main: '#0ea5e9',
    dark: '#1e40af',
    contrastText: '#ffffff'
  }
};

// Portal-Specific Themes
export const portalThemes = {
  client: {
    name: 'Healthcare Provider Portal',
    primary: brandColors.primary[600],
    primaryLight: brandColors.primary[500],
    primaryDark: brandColors.primary[700],
    primaryContrast: '#ffffff',
    
    accent: brandColors.primary[400],
    hover: brandColors.primary[100],
    active: brandColors.primary[200],
    
    navbar: {
      background: brandColors.primary[600],
      text: '#ffffff',
      hover: 'rgba(255, 255, 255, 0.1)'
    },
    
    sidebar: {
      background: '#ffffff',
      activeItem: brandColors.primary[100],
      hoverItem: brandColors.primary[50],
      text: brandColors.tertiary[700],
      activeText: brandColors.primary[700]
    },
    
    special: {
      vip: '#ec4899',
      premium: '#f59e0b'
    }
  },
  
  internal: {
    name: 'Operations Portal',
    primary: brandColors.tertiary[800],
    primaryLight: brandColors.tertiary[700],
    primaryDark: brandColors.tertiary[900],
    primaryContrast: '#ffffff',
    
    accent: brandColors.secondary[500],
    hover: brandColors.tertiary[100],
    active: brandColors.tertiary[200],
    
    navbar: {
      background: brandColors.tertiary[800],
      gradient: `linear-gradient(135deg, ${brandColors.tertiary[800]} 0%, ${brandColors.tertiary[700]} 100%)`,
      text: '#ffffff',
      hover: 'rgba(255, 255, 255, 0.1)'
    },
    
    sidebar: {
      background: '#ffffff',
      activeItem: brandColors.secondary[100],
      hoverItem: '#f6f7f6',
      text: brandColors.tertiary[700],
      activeText: brandColors.secondary[700]
    },
    
    special: {
      admin: brandColors.tertiary[800],
      alert: semanticColors.error.main
    }
  },
  
  rep: {
    name: 'Sales Representative Portal',
    primary: brandColors.primary[600],
    primaryLight: brandColors.primary[500],
    primaryDark: brandColors.primary[700],
    primaryContrast: '#ffffff',
    
    commission: {
      background: brandColors.primary[700],
      earned: semanticColors.success.main,
      pending: semanticColors.warning.main,
      progressBg: 'rgba(255, 255, 255, 0.15)'
    },
    
    navbar: {
      background: brandColors.primary[700],
      text: '#ffffff',
      hover: 'rgba(255, 255, 255, 0.1)'
    },
    
    sidebar: {
      background: brandColors.primary[600],
      activeItem: 'rgba(255, 255, 255, 0.15)',
      hoverItem: 'rgba(255, 255, 255, 0.1)',
      text: '#ffffff',
      activeText: '#ffffff'
    },
    
    special: {
      celebration: `linear-gradient(90deg, ${semanticColors.success.main}, #16a34a)`,
      achievement: semanticColors.warning.main
    }
  }
};

// Neutral Colors
export const neutralColors = {
  white: '#ffffff',
  black: '#000000',
  
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
    950: '#030712'
  }
};

// Backgrounds
export const backgrounds = {
  default: neutralColors.gray[50],
  paper: neutralColors.white,
  elevation: {
    1: '#fcfcfd',
    2: '#f8fafc',
    3: '#f3f4f6'
  },
  overlay: 'rgba(0, 0, 0, 0.5)',
  gradient: {
    light: 'linear-gradient(180deg, #ffffff 0%, #f0fdfa 50%, #ffffff 100%)',
    medical: `linear-gradient(135deg, ${brandColors.primary[100]} 0%, ${brandColors.secondary[100]} 100%)`,
    dark: `linear-gradient(135deg, ${brandColors.tertiary[800]} 0%, ${brandColors.tertiary[700]} 100%)`
  }
};

// Text Colors
export const textColors = {
  primary: neutralColors.gray[900],
  secondary: neutralColors.gray[600],
  disabled: neutralColors.gray[400],
  hint: neutralColors.gray[300],
  inverse: neutralColors.white
};

// Special Effects
export const effects = {
  shadow: {
    primary: `0 4px 14px ${brandColors.primary[600]}40`,
    primaryHover: `0 6px 20px ${brandColors.primary[600]}50`,
    secondary: `0 4px 14px ${brandColors.secondary[500]}40`,
    success: `0 2px 8px ${semanticColors.success.main}40`,
    warning: `0 2px 8px ${semanticColors.warning.main}40`,
    card: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    cardHover: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  },
  
  glow: {
    primary: `0 0 20px ${brandColors.primary[600]}40`,
    success: `0 0 20px ${semanticColors.success.main}40`,
    warning: `0 0 20px ${semanticColors.warning.main}40`
  },
  
  gradient: {
    shine: 'linear-gradient(45deg, transparent, rgba(255,255,255,0.3), transparent)',
    rainbow: 'linear-gradient(45deg, #667eea 0%, #764ba2 50%, #f093fb 100%)'
  }
};

// Get current portal theme
export const getCurrentPortalTheme = () => {
  const path = window.location.pathname;
  if (path.includes('/internal')) return portalThemes.internal;
  if (path.includes('/rep')) return portalThemes.rep;
  return portalThemes.client; // default
};

// Export a function to get colors for current portal
export const getPortalColors = (portalType = null) => {
  const portal = portalType || getCurrentPortalTheme();
  return {
    ...portal,
    semantic: semanticColors,
    neutral: neutralColors,
    background: backgrounds,
    text: textColors,
    effects: effects
  };
};
